/**
 * useTooltipDirection
 *
 * Hook para detectar la dirección de navegación en el sidebar basado en índices.
 *
 * Direction-aware animations:
 * - Si navegamos a un item con índice mayor → direction: 'down'
 * - Si navegamos a un item con índice menor → direction: 'up'
 * - Primera navegación → direction: 'down' (default)
 *
 * @example
 * ```typescript
 * const { getDirection, currentIndex } = useTooltipDirection();
 *
 * // Al cambiar de item
 * const direction = getDirection(newItemIndex);
 * // direction = 'up' | 'down'
 * ```
 */

import { useRef } from 'react';

export type TooltipDirection = 'up' | 'down';

export interface UseTooltipDirectionReturn {
  getDirection: (currentIndex: number) => TooltipDirection;
  currentIndex: number | null;
  previousIndex: number | null;
  reset: () => void;
}

export function useTooltipDirection(): UseTooltipDirectionReturn {
  const prevIndexRef = useRef<number | null>(null);
  const currentIndexRef = useRef<number | null>(null);

  /**
   * Determina la dirección basada en el índice del item actual
   */
  const getDirection = (currentIndex: number): TooltipDirection => {
    const prev = prevIndexRef.current;

    prevIndexRef.current = currentIndexRef.current;
    currentIndexRef.current = currentIndex;

    if (prev === null) {
      return 'down';
    }

    return currentIndex > prev ? 'down' : 'up';
  };

  /**
   * Resetea el estado (útil para testing o unmount)
   */
  const reset = () => {
    prevIndexRef.current = null;
    currentIndexRef.current = null;
  };

  return {
    getDirection,
    currentIndex: currentIndexRef.current,
    previousIndex: prevIndexRef.current,
    reset,
  };
}

export default useTooltipDirection;
